import { useState, useRef, useEffect, KeyboardEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Paperclip, Clock, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useChatStore } from "../lib/useChatStore";
import { sendAssistantMessage, createConversation } from "../lib/api";
import FileThumbnail from "@/components/chat/FileThumbnail";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

interface ChatInputProps {
  useWeb: boolean;
}

const ChatInput = ({ useWeb }: ChatInputProps) => {
  const [text, setText] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
const [isLimitBlocked, setIsLimitBlocked] = useState(false);
const [nextAvailableTime, setNextAvailableTime] = useState<Date | null>(null);
const [timeLeft, setTimeLeft] = useState<string>("");
const [isProcessingFiles, setIsProcessingFiles] = useState(false);
const [fileProcessingProgress, setFileProcessingProgress] = useState(0);
const [fileProcessingStage, setFileProcessingStage] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeConversationId, addMessage, setStreaming, setActiveConversation, clearMessages, setConversations, isStreaming, setMessages, conversations, pendingInitialMessage, setPendingInitialMessage } = useChatStore();
  const pendingProcessedRef = useRef(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      const baseHeight = 45;
      const maxHeight = 200;
      
      // ✅ Só calcular scrollHeight se houver texto
      if (text.trim().length === 0) {
        // Textarea vazia = altura base fixa de 45px
        textareaRef.current.style.height = baseHeight + "px";
      } else {
        // Textarea com texto = auto-resize
        textareaRef.current.style.height = "auto";
        const newHeight = Math.max(baseHeight, Math.min(textareaRef.current.scrollHeight, maxHeight));
        textareaRef.current.style.height = newHeight + "px";
      }
    }
  }, [text]);

  // Auto-focus textarea quando streaming terminar
  useEffect(() => {
    if (!isStreaming && textareaRef.current) {
      // Aguardar próximo frame para garantir que disabled foi removido
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [isStreaming]);

  // Auto-send pending initial message (e.g. from Jurisprudências "Perguntar com IA")
  useEffect(() => {
    if (pendingInitialMessage && !pendingProcessedRef.current && !isStreaming) {
      pendingProcessedRef.current = true;
      setPendingInitialMessage(null);
      // Set text and trigger send after state settles
      const sendInitial = async () => {
        setText(pendingInitialMessage);
        // Need to directly call the send logic with the text
        // since setText is async and handleSend reads from state
        const userMessage = pendingInitialMessage.trim();
        if (!userMessage) return;

        let conversationId = activeConversationId;
        if (!conversationId) {
          const initialTitle = userMessage.slice(0, 60) || "Nova conversa jurídica";
          const newConv = await createConversation(initialTitle);
          conversationId = newConv.id;
          const nowIso = new Date().toISOString();
          setConversations(prev => [
            {
              id: newConv.id,
              user_id: user?.id || newConv.user_id,
              title: newConv.title ?? initialTitle,
              updated_at: nowIso,
              deleted_at: null
            },
            ...prev
          ]);
          setActiveConversation(conversationId);
          clearMessages();
          navigate(`/chat-juridico/${conversationId}`, { replace: true });
        }

        addMessage({ role: "user", content: userMessage });
        addMessage({ role: "assistant", content: "" });
        setStreaming(true);

        try {
          const data = await sendAssistantMessage({
            conversationId: conversationId!,
            text: userMessage,
          });

          addMessage({ role: "assistant", content: data.response || "" }, true);
          setStreaming(false);

          // Recarregar mensagens do banco para obter id e created_at
          if (conversationId) {
            const { loadMessages } = await import("../lib/api");
            const updatedMessages = await loadMessages(conversationId);
            setMessages(updatedMessages);
          }
        } catch (err: any) {
          console.error("Erro ao enviar mensagem inicial:", err);
          setStreaming(false);
          addMessage({ role: "assistant", content: "Desculpe, ocorreu um erro ao processar sua mensagem." }, true);
        }

        setText("");
      };
      sendInitial();
    }
  }, [pendingInitialMessage, isStreaming]);

  // Remove pending file
  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle file paste from clipboard (CTRL+V)
  const handlePasteFile = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData || !clipboardData.files) return;
    
    const files = Array.from(clipboardData.files);
    
    const validTypes = [
      'application/pdf',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    const validFiles = files.filter(file => validTypes.includes(file.type));
    
    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
      e.preventDefault();
    }
  };

  // Handle attachment click
  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  // ✅ FASE 5: Verificar limite 3 mensagens/24h (plano gratuito)
  const checkMessageLimit = useCallback(async (): Promise<void> => {
    if (!user) {
      setIsLimitBlocked(false);
      return;
    }

    // Verificar plano do usuário — pegar a mais recente ativa
    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('plan_type')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const isFreePlan = !subscription || subscription.plan_type === 'free';
    if (!isFreePlan) {
      setIsLimitBlocked(false);
      setNextAvailableTime(null);
      return;
    }

    // Contar mensagens nas últimas 24h — APENAS do próprio usuário
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    // Buscar conversas do usuário primeiro
    const { data: userConvs } = await supabase
      .from('legal_conversations')
      .select('id')
      .eq('user_id', user.id);

    const userConvIds = (userConvs || []).map(c => c.id);

    if (userConvIds.length === 0) {
      setIsLimitBlocked(false);
      setNextAvailableTime(null);
      return;
    }

    const { count } = await supabase
      .from('legal_messages')
      .select('id', { count: 'exact', head: true })
      .eq('role', 'user')
      .in('conversation_id', userConvIds)
      .gte('created_at', twentyFourHoursAgo);

    if (count && count >= 3) {
      // Buscar a mensagem mais antiga das últimas 24h para calcular o tempo exato
      const { data: oldestMessage } = await supabase
        .from('legal_messages')
        .select('created_at')
        .eq('role', 'user')
        .in('conversation_id', userConvIds)
        .gte('created_at', twentyFourHoursAgo)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (oldestMessage) {
        // Próxima mensagem disponível = 24h após a mensagem mais antiga
        const nextTime = new Date(new Date(oldestMessage.created_at).getTime() + 24 * 60 * 60 * 1000);
        setNextAvailableTime(nextTime);
        setIsLimitBlocked(true);
      }
    } else {
      setIsLimitBlocked(false);
      setNextAvailableTime(null);
    }
  }, [user]);

  // Verificação periódica do limite a cada minuto
  useEffect(() => {
    checkMessageLimit();
    
    const interval = setInterval(checkMessageLimit, 60000);
    
    return () => clearInterval(interval);
  }, [checkMessageLimit]);

  // Contador regressivo em tempo real
  useEffect(() => {
    if (!nextAvailableTime) {
      setTimeLeft("");
      return;
    }
    
    const updateCountdown = () => {
      const now = new Date();
      if (now >= nextAvailableTime) {
        setIsLimitBlocked(false);
        setNextAvailableTime(null);
        setTimeLeft("");
        return;
      }

      const diff = nextAvailableTime.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateCountdown();
    const countdownInterval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(countdownInterval);
  }, [nextAvailableTime]);

  const handleSend = useCallback(async () => {
    if (isLimitBlocked) {
      toast.error(`Limite de 3 perguntas em 24h atingido. Próxima pergunta disponível em ${timeLeft}.`, {
        duration: 8000,
        action: {
          label: "Ver Planos",
          onClick: () => navigate("/planos")
        }
      });
      return;
    }
    
    // Validate if there's text OR files
    if (!text.trim() && pendingFiles.length === 0) return;

    // PROCESSAMENTO DE ARQUIVOS
    if (pendingFiles.length > 0) {
      setIsProcessingFiles(true);
      setFileProcessingStage(`Processando ${pendingFiles.length} arquivo(s)...`);
      setFileProcessingProgress(0);
    }

    const userMessage = text.trim();
    const allFiles: File[] = [...pendingFiles];

    setText("");
    setPendingFiles([]);

    // Auto-criar conversa se não houver uma ativa
    let conversationId = activeConversationId;
    if (!conversationId) {
      const nowIso = new Date().toISOString();
      const initialTitle = userMessage.trim().slice(0, 60) || "Nova conversa jurídica";
      
      const newConv = await createConversation(initialTitle);
      conversationId = newConv.id;
      
      // ✅ Atualizar histórico local instantaneamente (sem API extra)
      setConversations(prev => [
        {
          id: newConv.id,
          user_id: user?.id || newConv.user_id,
          title: newConv.title ?? initialTitle,
          updated_at: nowIso,
          deleted_at: null
        },
        ...prev
      ]);
      
      setActiveConversation(conversationId);
      clearMessages();
      navigate(`/chat-juridico/${conversationId}`, { replace: true });
    }

    // Add user message
    const messageContent = userMessage || (allFiles.length > 0 ? `[${allFiles.length} arquivo(s)]` : "");
    addMessage({ role: "user", content: messageContent });
    addMessage({ role: "assistant", content: "" });
    
    setStreaming(true);

    try {
      const data = await sendAssistantMessage({
        conversationId: conversationId!,
        text: userMessage,
      });

      addMessage({ role: "assistant", content: data.response || "" }, true);
      setStreaming(false);

      // AUTO-GERAR TÍTULO se for genérico
      const currentConv = conversations.find(c => c.id === conversationId);
      const hasGenericTitle = !currentConv?.title || 
                              currentConv.title === "Nova conversa jurídica" ||
                              currentConv.title.startsWith("[");
      
      if (hasGenericTitle && userMessage.trim()) {
        const { generateConversationTitle } = await import("../lib/api");
        await generateConversationTitle(conversationId!, userMessage);
        
        setConversations(prev => prev.map(c => 
          c.id === conversationId 
            ? { 
                ...c, 
                title: userMessage.slice(0, 50) + (userMessage.length > 50 ? "..." : ""),
                updated_at: new Date().toISOString()
              }
            : c
        ));
      }
      
      // Recarregar mensagens do banco para obter id e created_at
      if (conversationId) {
        const { loadMessages } = await import("../lib/api");
        const updatedMessages = await loadMessages(conversationId);
        setMessages(updatedMessages);
      }

      checkMessageLimit();
    } catch (e) {
      console.error("Send error:", e);
      setStreaming(false);
      
      const errorMessage = e instanceof Error ? e.message : "Erro desconhecido";
      const isLimitError = errorMessage.toLowerCase().includes("limite") || errorMessage.toLowerCase().includes("créditos");
      
      addMessage({ 
        role: "assistant", 
        content: isLimitError 
          ? `⚠️ **Limite de créditos atingido**\n\n${errorMessage}\n\n💡 **Dica**: Acesse a página de Planos para fazer upgrade ou comprar créditos extras.`
          : `⚠️ Erro ao processar sua mensagem: ${errorMessage}. Por favor, tente novamente.`
      }, true);
    } finally {
      setIsProcessingFiles(false);
      setFileProcessingProgress(0);
      setFileProcessingStage("");
    }
  }, [text, useWeb, activeConversationId, pendingFiles, isLimitBlocked, timeLeft, checkMessageLimit, addMessage, setStreaming, setActiveConversation, setConversations, clearMessages, navigate, conversations, setMessages, user]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    if ((e as any).isComposing || isComposing) return;
    if (e.shiftKey || e.altKey) return;
    if (isStreaming) return;
    e.preventDefault();
    handleSend();
  };

  // ✅ Listen for suggestions from EmptyState (DEPOIS de handleSend estar definido)
  useEffect(() => {
    const handleSuggestion = (e: CustomEvent) => {
      const suggestionText = e.detail;
      setText(suggestionText);
      // Aguardar setState e textarea atualizar antes de enviar
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.value = suggestionText;
          }
          handleSend();
        }, 150);
      });
    };
    window.addEventListener("legalchat:suggestion" as any, handleSuggestion as any);
    return () => window.removeEventListener("legalchat:suggestion" as any, handleSuggestion as any);
  }, [handleSend]);

  return (
    <div className="space-y-3 px-4 pb-4">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* All Files Preview - Miniaturas uniformes */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-3 px-2">
          {pendingFiles.map((file, idx) => (
            <FileThumbnail
              key={idx}
              file={file}
              onRemove={() => handleRemovePendingFile(idx)}
              disabled={isStreaming}
            />
          ))}
        </div>
      )}

      {/* Indicador de processamento de arquivos */}
      {isProcessingFiles && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-4xl mx-auto">
          <FileText className="h-4 w-4 text-blue-600 dark:text-blue-500 animate-pulse" />
          <div className="flex-1">
            <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
              {fileProcessingStage}
            </p>
            <Progress value={fileProcessingProgress} className="h-1 mt-1" />
          </div>
        </div>
      )}

      {/* Input Container */}
      <div className="relative max-w-4xl mx-auto">
        <div className="flex-1 w-full relative rounded-3xl border bg-background shadow-sm hover:shadow-md transition-shadow">
          <Textarea
            ref={textareaRef}
            data-tour="chat-input-field"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onPaste={handlePasteFile}
            placeholder={
              isLimitBlocked 
                ? `Limite atingido. Próxima mensagem em ${timeLeft}` 
                : isStreaming 
                  ? "Aguarde a resposta da IA..." 
                  : "Pergunte qualquer coisa..."
            }
            disabled={isLimitBlocked || isStreaming}
            className="min-h-[40px] max-h-[200px] border-0 resize-none rounded-3xl px-5 py-3 pr-24 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          
          {/* Botões dentro do input */}
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            {/* Botão de Anexo (Clipe) */}
            <Button
              type="button"
              data-tour="attach-file-btn"
              onClick={handleAttachmentClick}
              disabled={isLimitBlocked || isStreaming}
              size="icon"
              variant="ghost"
              className="rounded-full w-9 h-9"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            
            {/* Botão de Enviar */}
            <Button
              onClick={handleSend}
              disabled={isLimitBlocked || isStreaming || (!text.trim() && pendingFiles.length === 0)}
              size="icon"
              className="rounded-full w-9 h-9"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Contador regressivo para plano gratuito */}
      {isLimitBlocked && timeLeft && (
        <div className="flex items-center justify-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg max-w-4xl mx-auto">
          <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
          <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">
            Próxima pergunta disponível em: <span className="font-mono font-bold">{timeLeft}</span>
          </p>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate("/planos")}
            className="text-yellow-700 dark:text-yellow-400 underline"
          >
            Fazer upgrade
          </Button>
        </div>
      )}

      <p className="text-xs text-center text-muted-foreground">
        {isLimitBlocked 
          ? `⏰ Limite de 3 perguntas/24h atingido • Próxima mensagem em ${timeLeft}` 
          : "Enter para enviar • Shift+Enter para nova linha • Ctrl+V para colar arquivos"
        }
      </p>
    </div>
  );
};

export default ChatInput;
